import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { deleteRecipient, Recipient, updateRecipient, updateRecipientStatus } from '@/lib/forward-api';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RecipientDetailViewProps {
    recipient: Recipient;
    onUpdate: () => void;
    onDelete: () => void;
}

export function RecipientDetailView({ recipient, onUpdate, onDelete }: RecipientDetailViewProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [editing, setEditing] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [formData, setFormData] = useState({
        title: recipient.title,
        forwardingNumber: recipient.forwardingNumber,
        active: recipient.active
    });

    // Update formData when recipient changes
    useEffect(() => {
        setFormData({
            title: recipient.title,
            forwardingNumber: recipient.forwardingNumber,
            active: recipient.active
        });
        setEditing(false); // Reset editing state when switching recipients
    }, [recipient]);

    const handleSave = async () => {
        try {
            await updateRecipient(recipient.id, formData);
            toast({
                title: t('success.title'),
                description: t('forward.recipientUpdated')
            });
            setEditing(false);
            onUpdate();
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.recipientUpdateError'),
                variant: 'destructive'
            });
        }
    };

    const handleDelete = async () => {
        try {
            await deleteRecipient(recipient.id);
            toast({
                title: t('success.title'),
                description: t('forward.recipientDeleted')
            });
            onDelete();
        } catch (error: any) {
            const isInUse = error.response?.data?.errorCode === 'RECEIVER_IN_USE';
            toast({
                title: t('error.title'),
                description: isInUse
                    ? t('forward.recipientInUseError')
                    : t('forward.recipientDeleteError'),
                variant: 'destructive'
            });
        }
    };

    const handleToggleActive = async (active: boolean) => {
        try {
            await updateRecipientStatus(recipient.id, active);
            setFormData({ ...formData, active });
            toast({
                title: t('success.title'),
                description: t('forward.recipientStatusChanged', { status: active ? t('forward.active') : t('forward.inactive') })
            });
            onUpdate();
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.statusChangeError'),
                variant: 'destructive'
            });
        }
    };

    return (
        <div className="h-full overflow-y-auto bg-gray-50">
            <div className="py-6 px-6">
                {/* Header - Dezent, volle Breite */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-semibold text-gray-900">{recipient.title}</h1>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${formData.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {formData.active ? t('forward.active') : t('forward.inactive')}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={formData.active}
                            onCheckedChange={handleToggleActive}
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDeleteDialog(true)}
                            className="text-gray-400 hover:text-red-600"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Content - Kompaktes Formular */}
                <div className="bg-white border rounded-lg p-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <Label htmlFor="edit-title" className="text-sm font-medium text-gray-700 mb-2 block">
                                {t('global.fieldnames.name')}
                            </Label>
                            <Input
                                id="edit-title"
                                value={formData.title}
                                onChange={(e) => {
                                    setFormData({ ...formData, title: e.target.value });
                                    if (!editing) setEditing(true);
                                }}
                                className="h-10"
                                placeholder="z.B. Service Team"
                            />
                        </div>

                        <div>
                            <Label htmlFor="edit-phone" className="text-sm font-medium text-gray-700 mb-2 block">
                                {t('forward.phoneNumber')}
                            </Label>
                            <Input
                                id="edit-phone"
                                value={formData.forwardingNumber}
                                onChange={(e) => {
                                    setFormData({ ...formData, forwardingNumber: e.target.value });
                                    if (!editing) setEditing(true);
                                }}
                                className="h-10"
                                placeholder="+49..."
                            />
                        </div>
                    </div>

                    {/* Actions - nur anzeigen wenn bearbeitet */}
                    {editing && (
                        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setEditing(false);
                                    setFormData({
                                        title: recipient.title,
                                        forwardingNumber: recipient.forwardingNumber,
                                        active: recipient.active
                                    });
                                }}
                            >
                                {t('global.button.cancel')}
                            </Button>
                            <Button size="sm" onClick={handleSave}>
                                {t('global.button.save')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('forward.deleteRecipient')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('forward.deleteRecipientConfirm')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('global.button.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>
                            {t('global.button.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
